import { Link } from "react-router-dom";
import {
  getContactEmailLabel,
  useContactEmail,
} from "./PublicSiteComponents";
import "./LandingPage.css";

const companyName = "Simplis Software Solutions LLC";
const platformName = "Nexus Game Engine";
const supportEmailToken = "__NEXUS_CONTACT_EMAIL__";
const supportEmail = supportEmailToken;
const siteUrl = "nexusclubengine.com";
const lastUpdated = "May 16, 2026";

const pages = {
  terms: {
    label: "Terms of Service",
    intro:
      "These Terms of Service govern access to and use of the Nexus Game Engine platform operated by Simplis Software Solutions LLC, a North Carolina limited liability company.",
    sections: [
      {
        title: "1. Agreement to These Terms",
        body: [
          `By creating an account, starting a trial, subscribing, accessing a workspace, or using the ${platformName} platform, you agree to these Terms on behalf of the business or private organization you represent. If you do not have authority to bind that organization, you may not use the Services.`,
          "These Terms apply to the web dashboard, Nexus Mobile application, integrated storefronts, subscription checkout flows, administrative tools, analytics features, communications, and related services we provide, collectively called the Services.",
        ],
      },
      {
        title: "2. Business Use Only; Age Requirement",
        body: [
          "The Services are intended only for businesses, private organizations, owners, administrators, operators, and authorized staff using the platform for internal organizational administration. The Services are not intended for consumer, household, or personal use.",
          "You must be at least 18 years old to access or use the Services. You are responsible for ensuring that all staff, administrators, and invited users in your workspace satisfy this age requirement and comply with these Terms.",
        ],
      },
      {
        title: "3. Description of the Services",
        body: [
          `${platformName} provides administrative software and productivity tools for private membership organizations. The Services may include member directories, staff permissions, scheduling tools, RSVP and waitlist tools, loyalty or promotional tracking, internal ledgering, operating account records, analytics, reporting, invite workflows, mobile access, and merchandise storefront features.`,
          "The Services are software tools only. They do not create, operate, manage, supervise, endorse, audit, validate, approve, or participate in any customer's physical or digital organization, events, business model, internal operations, member relationships, or compliance obligations.",
        ],
      },
      {
        title: "4. Customer Responsibility for Operations and Compliance",
        body: [
          "You are solely responsible for how you configure, administer, and use your workspace. You are solely responsible for all activity conducted by your owners, administrators, operators, staff, contractors, invited users, and anyone else who accesses your workspace through credentials, links, devices, or permissions you control.",
          "You are solely responsible for ensuring that your organization, activities, records, promotions, events, communications, and use of the Services comply with all applicable local, state, federal, and international laws, rules, licensing requirements, tax obligations, employment obligations, privacy obligations, and organizational compliance requirements.",
          `${companyName} does not provide legal, tax, accounting, regulatory, employment, or compliance advice. The Services may help you organize records, but they do not determine whether your organization or use case is lawful, licensed, approved, or compliant.`,
        ],
      },
      {
        title: "5. Administrative Records; No Financial Product",
        body: [
          "Administrative ledgers, internal wallets, operating accounts, loyalty balances, promotional records, member balances, and similar fields inside the Services are customer-entered recordkeeping fields only. They are provided to help customers maintain internal administrative records.",
          `${companyName} does not hold, transfer, custody, escrow, settle, transmit, convert, redeem, withdraw, or process member funds in any capacity. Ledger balances do not represent bank accounts, stored value, payment accounts, money transmission, deposit accounts, securities, credit products, financial products, or any entitlement against ${companyName}.`,
          "You are solely responsible for the accuracy, legality, classification, and use of all ledger entries, balances, schedules, permissions, notes, member records, reports, and other information entered into the Services.",
        ],
      },
      {
        title: "6. Payments, Subscriptions, Trials, and Storefronts",
        body: [
          "Subscriptions are billed monthly or annually through Stripe or another payment processor we designate. A 14-day free trial may be offered for eligible plans. Subscription checkout, payment authorization, invoices, payment method storage, and related payment operations may be handled by Stripe.",
          "The Services process B2B subscription payments and may process merchandise purchases through integrated storefronts. The Services do not process customer-to-member or member-to-member payments.",
          "Unless required by law, all payments are non-refundable. Upgrades may be prorated where supported by our billing system. Downgrades and cancellations take effect according to the applicable plan and billing cycle and do not entitle you to refunds for unused time.",
          "We may change pricing, plan features, trial eligibility, billing intervals, payment methods, or subscription terms with notice to the administrator email associated with your account. Unpaid or delinquent accounts may be suspended, downgraded, or terminated after a 5-day grace period.",
        ],
      },
      {
        title: "7. Account Administration and Security",
        body: [
          "You are responsible for maintaining accurate account information, safeguarding credentials, configuring role-based permissions, removing users who should no longer have access, reviewing staff access, and ensuring that workspace activity is authorized.",
          `You must promptly notify us at ${supportEmail} if you believe your account, workspace, credentials, or data may have been compromised. You are responsible for losses, claims, or issues arising from unauthorized access caused by your failure to secure credentials, devices, permissions, or invite links.`,
        ],
      },
      {
        title: "8. Customer Data and Consent",
        body: [
          "As between you and Simplis, you own the member data, organization records, ledger inputs, schedules, notes, and other information you submit to the Services. You grant Simplis a limited license to host, process, transmit, display, analyze, and otherwise use that data only as necessary to provide, secure, support, maintain, and improve the Services.",
          "You represent and warrant that you have all rights, notices, permissions, and consents required to enter personal data into the Organization Hub or any other part of the Services, including consent from members, staff, and invited users where required by law.",
          "You may not use the Services to collect or store sensitive personal information unless you are legally authorized to do so and have our prior written approval. Sensitive personal information includes medical records, financial account numbers, government identification numbers, biometric information, and similarly sensitive regulated data.",
        ],
      },
      {
        title: "9. Ownership of the Services",
        body: [
          `${companyName} owns and retains all rights, title, and interest in and to the ${platformName} software, web dashboard, Nexus Mobile application, storefront functionality, source code, object code, architecture, workflows, designs, interfaces, trademarks, trade names, analytics models, documentation, and related intellectual property.`,
          "You may not copy, modify, reverse engineer, decompile, resell, sublicense, lease, exploit, or create derivative works from the Services except as expressly permitted in writing by Simplis.",
        ],
      },
      {
        title: "10. Aggregated and De-Identified Data",
        body: [
          "We may create aggregated, anonymized, or de-identified information from use of the Services to improve analytics, platform health, reliability, performance, fraud prevention, security, and product development. We will not use aggregated or de-identified information to identify your organization, members, staff, or individual users.",
        ],
      },
      {
        title: "11. Acceptable Use and Suspension Rights",
        body: [
          "You must comply with the Acceptable Use Policy. We may suspend, restrict, or terminate access immediately, with or without notice and without refund, if we believe the Services are being used unlawfully, harmfully, deceptively, abusively, in a way that creates legal risk, in a way that threatens platform security, or in violation of these Terms or the Acceptable Use Policy.",
          "We may also take emergency action to protect the Services, users, infrastructure, service providers, payment processors, hosting providers, email providers, or the public from security threats, denial-of-service activity, malware, unauthorized access, severe legal risk, or operational harm.",
        ],
      },
      {
        title: "12. Service Availability and Changes",
        body: [
          "The Services may change over time. We may add, remove, suspend, modify, limit, replace, or discontinue features, plans, integrations, trial offers, storefront items, analytics, or mobile functionality at any time. We do not guarantee uninterrupted, error-free, or permanent availability.",
          "We may perform maintenance, updates, migrations, or security work that temporarily affects availability. We are not liable for downtime, delays, data entry errors, customer configuration mistakes, third-party outages, or unavailable integrations.",
        ],
      },
      {
        title: "13. Termination and Data Deletion",
        body: [
          "You may cancel your subscription according to the billing tools we make available. Upon cancellation, nonpayment, suspension, or termination, access to your workspace may be revoked immediately or at the end of the applicable billing period, depending on the circumstances.",
          "After termination, cancellation, or prolonged inactivity, workspace data may be scheduled for permanent deletion according to our operational retention practices, backup cycles, and legal obligations. You are responsible for exporting any records you need before cancellation or termination.",
        ],
      },
      {
        title: "14. Disclaimers",
        body: [
          "The Services are provided strictly 'as is' and 'as available.' To the maximum extent permitted by law, Simplis disclaims all warranties, express, implied, statutory, or otherwise, including warranties of merchantability, fitness for a particular purpose, title, non-infringement, availability, accuracy, reliability, security, and error-free operation.",
          "Simplis does not warrant that the Services will satisfy any particular regulatory, tax, employment, licensing, operational, accounting, or compliance requirement. You are solely responsible for independent review of your legal and operational obligations.",
        ],
      },
      {
        title: "15. Limitation of Liability",
        body: [
          "To the maximum extent permitted by law, Simplis will not be liable for indirect, incidental, special, consequential, exemplary, punitive, or enhanced damages, lost profits, lost revenue, loss of goodwill, business interruption, loss of data, inaccurate records, legal exposure, operational decisions, or third-party claims, even if we have been advised of the possibility of those damages.",
          "To the maximum extent permitted by law, Simplis's total aggregate liability for all claims arising out of or relating to the Services or these Terms will not exceed the amount you paid to Simplis for the Services during the 12 months immediately preceding the event giving rise to the claim.",
        ],
      },
      {
        title: "16. Indemnification",
        body: [
          `You will defend, indemnify, and hold harmless ${companyName}, its owners, officers, employees, contractors, service providers, affiliates, successors, and assigns from and against any claims, demands, investigations, losses, liabilities, damages, penalties, fines, costs, and expenses, including reasonable attorneys' fees, arising from or related to:`,
        ],
        bullets: [
          "your organization, events, activities, staff, members, operations, communications, or business practices;",
          "your use or misuse of the Services;",
          "your violation of law, regulation, licensing requirements, these Terms, or the Acceptable Use Policy;",
          "data, records, ledger entries, schedules, permissions, or content entered by you or your users;",
          "claims that your organization or activity was unlawful, unauthorized, harmful, deceptive, or improperly licensed;",
          "your failure to obtain required consents or rights before entering personal data into the Services.",
        ],
      },
      {
        title: "17. Informal Resolution, Arbitration, and Class Waiver",
        body: [
          `Before starting arbitration or any other proceeding, you must email ${supportEmail} with a written description of the dispute and allow 30 days for informal resolution. The parties will attempt in good faith to resolve the dispute during that period.`,
          "Except for claims that may be brought in small claims court or claims seeking emergency injunctive relief, disputes arising out of or relating to these Terms or the Services will be resolved by binding individual arbitration. You and Simplis waive the right to a jury trial and waive the right to participate in a class action, class arbitration, representative action, or consolidated proceeding.",
          "The arbitration will be conducted in North Carolina unless the parties agree otherwise. The arbitrator may award relief only on an individual basis and only to the extent necessary to resolve the individual claim.",
        ],
      },
      {
        title: "18. Governing Law and Venue",
        body: [
          "These Terms are governed by the laws of the State of North Carolina, without regard to conflict-of-law principles. For disputes not subject to arbitration, the exclusive venue will be the state or federal courts located in North Carolina, and you consent to personal jurisdiction there.",
        ],
      },
      {
        title: "19. Updates to These Terms",
        body: [
          `We may update these Terms at any time by posting a revised version on ${siteUrl} or within the Services. Continued use of the Services after updates are posted constitutes acceptance of the updated Terms. If you do not agree to updated Terms, you must stop using the Services and cancel your subscription.`,
        ],
      },
      {
        title: "20. Contact",
        body: [
          `Questions about these Terms may be sent to ${supportEmail}.`,
        ],
      },
    ],
  },
  privacy: {
    label: "Privacy Policy",
    intro:
      "This Privacy Policy explains how Simplis Software Solutions LLC collects, uses, shares, and protects information in connection with the Nexus Game Engine platform.",
    sections: [
      {
        title: "1. Scope",
        body: [
          `This Privacy Policy applies to ${platformName}, Nexus Mobile, ${siteUrl}, integrated storefronts, administrative dashboards, and related services operated by ${companyName}.`,
          "The Services are designed for business and private organization use. Customers are responsible for providing any required notices to their staff, members, invited users, and other individuals whose information they enter into the Services.",
        ],
      },
      {
        title: "2. Information We Collect",
        body: [
          "We may collect information directly from administrators, staff, invited users, and customers, as well as information entered by customers into their workspaces. This may include names, email addresses, phone numbers, organization names, usernames, passwords or password hashes, roles, permissions, RSVP status, schedule data, loyalty point balances, referral information, internal notes, system activity history, login times, and administrative records.",
          "We may collect billing and subscription metadata, plan selections, trial status, invoice identifiers, checkout status, merchandise order metadata, and payment processor references. Payment method details are handled by Stripe or another payment processor and are not stored directly by Simplis.",
        ],
      },
      {
        title: "3. Mobile App and Device Information",
        body: [
          "Nexus Mobile may collect device information, device identifiers, app version, operating system information, push notification tokens, authentication state, basic app usage analytics, and crash or performance data. This information helps us operate the app, deliver notifications, secure accounts, troubleshoot issues, and improve reliability.",
          "We do not intentionally collect precise location data, contacts, photos, camera data, or microphone data unless a future feature clearly requests permission and you choose to provide it.",
        ],
      },
      {
        title: "4. Cookies and Analytics",
        body: [
          "We may use cookies, local storage, tokens, session identifiers, and similar technologies for authentication, session management, security, preferences, account continuity, analytics, and product improvement.",
          "We may use standard SaaS analytics to understand feature usage, errors, performance, traffic patterns, and platform health. Analytics are used to improve the Services and are not used to sell personal information to data brokers.",
        ],
      },
      {
        title: "5. How We Use Information",
        body: [
          "We use information to provide, operate, secure, maintain, troubleshoot, support, and improve the Services; create and manage accounts; process subscriptions and storefront orders; send transactional emails; enforce Terms and policies; respond to support requests; prevent abuse; analyze platform performance; and comply with legal obligations.",
          "We may use organization and usage information to improve analytics models, system reliability, onboarding, documentation, product design, and platform health, including through aggregated or de-identified data.",
        ],
      },
      {
        title: "6. Customer Responsibilities for Personal Data",
        body: [
          "Customers decide what information they enter into the Services and are responsible for the accuracy, legality, and appropriateness of that information. Customers must obtain all required consents and provide all required notices before entering member, staff, or invited user data into the Organization Hub or any other part of the Services.",
          "Customers may not use the Services to collect or store sensitive personal information such as medical records, financial account numbers, government identification numbers, biometric information, or similarly sensitive regulated data unless they are legally authorized and have our prior written approval.",
        ],
      },
      {
        title: "7. How We Share Information",
        body: [
          "We may share information with vendors and service providers that help us operate the Services. These may include Stripe for payments, Render for hosting, Resend for transactional emails, analytics providers, security providers, customer support tools, and other infrastructure or operational vendors.",
          "We may disclose information if required by law, subpoena, court order, legal process, government request, or to protect the rights, safety, property, security, or integrity of Simplis, customers, users, service providers, or the public.",
          "We may transfer information in connection with a merger, acquisition, financing, restructuring, sale of assets, bankruptcy, or similar business transaction, subject to appropriate confidentiality and legal protections.",
        ],
      },
      {
        title: "8. We Do Not Sell Personal Information",
        body: [
          "We do not sell personal information to data brokers. We do not knowingly sell personal information for cross-context behavioral advertising. If our practices change, we will update this Privacy Policy as required by law.",
        ],
      },
      {
        title: "9. Data Security",
        body: [
          "We use reasonable administrative, technical, and organizational safeguards designed to protect information processed through the Services. These may include authentication, role-based access, access controls, encrypted transmission where supported, service provider controls, monitoring, and operational security practices.",
          "No software, network, database, or transmission over the internet is completely secure. We cannot guarantee absolute security, and customers remain responsible for managing permissions, safeguarding credentials, and limiting access to authorized users.",
        ],
      },
      {
        title: "10. Data Retention",
        body: [
          "We retain information for as long as reasonably necessary to provide the Services, maintain accounts, comply with legal obligations, resolve disputes, enforce agreements, maintain backups, prevent abuse, and support legitimate business purposes.",
          "After cancellation, termination, or prolonged inactivity, workspace data may be scheduled for deletion according to our operational retention practices, backup cycles, and legal requirements.",
        ],
      },
      {
        title: "11. Access, Correction, Export, and Deletion Requests",
        body: [
          `Administrators and authorized users may request access, correction, export, or deletion of information by emailing ${supportEmail}. We may need to verify your identity and authority before fulfilling a request. Some information may be retained where required for legal, security, billing, dispute-resolution, backup, or compliance purposes.`,
          "If your information was entered by a customer organization, we may direct you to that organization because it controls the workspace and determines how the information is used.",
        ],
      },
      {
        title: "12. Children's Privacy",
        body: [
          "The Services are intended only for users who are at least 18 years old. We do not knowingly collect personal information from anyone under 18. If you believe information from a person under 18 has been entered into the Services, contact us so we can review the issue.",
        ],
      },
      {
        title: "13. International Use",
        body: [
          "The Services are operated from the United States. If you access the Services from outside the United States, you understand that information may be processed in the United States or other locations where our service providers operate.",
        ],
      },
      {
        title: "14. Updates to This Privacy Policy",
        body: [
          `We may update this Privacy Policy at any time by posting a revised version on ${siteUrl} or within the Services. Continued use of the Services after updates are posted means the updated Privacy Policy applies to future use of the Services.`,
        ],
      },
      {
        title: "15. Contact",
        body: [
          `Privacy questions and data requests may be sent to ${supportEmail}.`,
        ],
      },
    ],
  },
  acceptableUse: {
    label: "Acceptable Use Policy",
    intro:
      "This Acceptable Use Policy explains what customers may and may not do with the Nexus Game Engine platform.",
    sections: [
      {
        title: "1. Purpose of the Services",
        body: [
          `${platformName} is intended for lawful administrative, productivity, scheduling, communications, role management, reporting, internal recordkeeping, promotional tracking, and organization management purposes for business and private organization customers.`,
          "The Services are tools. Customers remain solely responsible for their operations, compliance obligations, staff activity, member relationships, records, and decisions.",
        ],
      },
      {
        title: "2. Customer Accountability",
        body: [
          "You are responsible for all activity in your workspace, including activity by owners, administrators, staff, contractors, invited users, and anyone using credentials or permissions you control. You may not allow any person or organization to use the Services in a way that violates this Acceptable Use Policy.",
          `${companyName} does not operate, supervise, audit, endorse, validate, approve, or participate in your organization or its activities. Use of the Services does not mean that Simplis has reviewed or approved your organization, activity, compliance posture, licensing status, records, or business practices.`,
        ],
      },
      {
        title: "3. Prohibited Conduct",
        body: [
          "You may not use the Services for unlawful, harmful, abusive, deceptive, unauthorized, or high-risk activity. Prohibited uses include:",
        ],
        bullets: [
          "violating any applicable law, regulation, tax obligation, licensing requirement, court order, or contractual duty;",
          "conducting regulated activity without all required authorization, licenses, registrations, approvals, and compliance controls;",
          "using the Services to circumvent local, state, federal, or organizational compliance requirements;",
          "fraud, deception, misrepresentation, identity misuse, unauthorized access, or impersonation;",
          "money laundering, sanctions evasion, tax evasion, or concealment of unlawful proceeds;",
          "harassment, threats, exploitation, hate, violence, or abusive conduct;",
          "uploading malware, harmful code, phishing content, spam, or deceptive communications;",
          "scraping, crawling, harvesting, probing, scanning, or testing the Services without written authorization;",
          "interfering with platform security, availability, integrity, rate limits, authentication systems, or infrastructure;",
          "infringing intellectual property, privacy, publicity, or other rights of any person or entity.",
        ],
      },
      {
        title: "4. No Member Fund Processing",
        body: [
          "You may not use the Services to hold, custody, escrow, transfer, settle, transmit, redeem, or process member funds. Administrative ledgers, operating accounts, internal wallets, promotional balances, loyalty balances, and similar features are recordkeeping fields only.",
          "You may not represent ledger balances as bank accounts, stored value, payment accounts, payment processing, money transmission, credit, deposits, securities, financial products, or an obligation of Simplis.",
        ],
      },
      {
        title: "5. No Misrepresentation of Simplis or Nexus",
        body: [
          `You may not represent that ${companyName}, ${platformName}, Nexus Mobile, or the Services are a regulator, auditor, licensing authority, compliance advisor, financial institution, payment intermediary, legal advisor, or entity that approves, validates, supervises, or guarantees the legality of your organization.`,
          "You may not use our name, marks, screenshots, product language, emails, checkout flows, support communications, or platform access to imply endorsement, certification, authorization, or legal approval by Simplis.",
        ],
      },
      {
        title: "6. Data and Privacy Restrictions",
        body: [
          "You may not enter personal data into the Services unless you have all required rights, consents, notices, and lawful bases to do so. You are responsible for responding to individuals whose information you enter and for honoring applicable privacy rights.",
          "You may not use the Services to collect or store medical records, financial account numbers, government identification numbers, biometric information, information about children, or similarly sensitive regulated data unless legally authorized and approved by Simplis in writing.",
        ],
      },
      {
        title: "7. Security Rules",
        body: [
          "You may not share administrator credentials, bypass access controls, create unauthorized integrations, attempt to access another customer's workspace, exploit bugs, overload infrastructure, or interfere with service availability.",
          "You must promptly notify us if you discover a security issue, unauthorized access, exposed credential, or misuse of the Services. You may not publicly disclose vulnerabilities without giving Simplis a reasonable opportunity to investigate and remediate.",
        ],
      },
      {
        title: "8. Monitoring and Enforcement",
        body: [
          "We may investigate suspected violations of this Acceptable Use Policy. We are not obligated to monitor every workspace or validate customer activity, but we may review account activity, metadata, support reports, service provider notices, payment processor notices, legal requests, or abuse indicators when necessary to protect the Services.",
          "We may suspend, restrict, remove, disable, or terminate access immediately, with or without notice and without refund, if we believe a violation has occurred or if continued access could create legal, security, operational, financial, reputational, or platform risk.",
        ],
      },
      {
        title: "9. Reporting Violations",
        body: [
          `Reports of suspected misuse may be sent to ${supportEmail}. Include the organization name, workspace information if available, a description of the issue, and any supporting details.`,
        ],
      },
      {
        title: "10. Updates to This Policy",
        body: [
          `We may update this Acceptable Use Policy at any time by posting a revised version on ${siteUrl} or within the Services. Continued use of the Services after updates are posted constitutes acceptance of the updated policy.`,
        ],
      },
    ],
  },
};

export default function LegalPage({ type }) {
  const page = pages[type] || pages.terms;
  const { contactEmail, isContactEmailLoaded } = useContactEmail();
  const supportEmailLabel =
    contactEmail || (isContactEmailLoaded ? getContactEmailLabel(contactEmail) : "");

  return (
    <main className="nexus-legal">
      <div className="nexus-legal__shell">
        <Link className="nexus-legal__back" to="/">
          Back to Nexus
        </Link>
        <p className="nexus-eyebrow">Nexus Game Engine</p>
        <h1>{page.label}</h1>
        <p>Last updated: {lastUpdated}</p>
        <p>{page.intro}</p>
        {page.sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            {section.body.map((paragraph) => (
              <p key={paragraph}>
                {paragraph.replaceAll(supportEmailToken, supportEmailLabel)}
              </p>
            ))}
            {section.bullets && (
              <ul>
                {section.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </main>
  );
}
